import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Coins, CheckCircle, ArrowRight, Loader2, CreditCard, TrendingUp, History, AlertCircle } from 'lucide-react';
import { API } from '@/App';

const PACK_ICONS = { credits_100: '💡', credits_500: '⚡', credits_2000: '🚀' };
const PACK_COLORS = { credits_100: '#35AFE4', credits_500: '#7c3aed', credits_2000: '#059669' };

export default function CreditsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [balance, setBalance] = useState(null);
  const [packs, setPacks] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const sessionId = searchParams.get('session_id');
  const isSuccess = window.location.pathname.includes('/credits/success');

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (sessionId && isSuccess) {
      pollStatus(sessionId);
    }
  }, [sessionId]);

  const loadData = async () => {
    try {
      const [balRes, packRes, histRes] = await Promise.all([
        axios.get(`${API}/credits/balance`, { headers }),
        axios.get(`${API}/credits/packs`),
        axios.get(`${API}/credits/history`, { headers }).catch(() => ({ data: [] })),
      ]);
      setBalance(balRes.data);
      setPacks(packRes.data);
      setHistory(histRes.data);
    } catch { }
    setLoading(false);
  };

  const pollStatus = async (sid) => {
    for (let i = 0; i < 10; i++) {
      try {
        const res = await axios.get(`${API}/credits/status/${sid}`);
        if (res.data.payment_status === 'paid') {
          loadData();
          return;
        }
      } catch { }
      await new Promise(r => setTimeout(r, 2000));
    }
    loadData();
  };

  const buyPack = async (lookupKey) => {
    setPurchasing(lookupKey);
    try {
      const res = await axios.post(`${API}/credits/checkout`, {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      }, { headers });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de la création du paiement";
      import('sonner').then(m => m.toast.error(msg));
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="credits-page">
      {/* Success banner */}
      {isSuccess && sessionId && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-6 flex items-center gap-3" data-testid="success-banner">
          <CheckCircle size={20} className="text-emerald-500" />
          <div>
            <p className="text-sm font-bold text-emerald-900">Paiement confirmé !</p>
            <p className="text-xs text-emerald-700">Vos crédits ont été ajoutés à votre solde.</p>
          </div>
        </div>
      )}

      {/* Balance card */}
      <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2a5a8f] rounded-2xl p-6 mb-6 text-white" data-testid="balance-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
            <Sparkles size={24} className="text-white" />
          </div>
          <div>
            <p className="text-sm text-white/60">Votre solde</p>
            <p className="text-3xl font-bold">{balance?.credits || 0} <span className="text-lg font-normal text-white/60">crédits</span></p>
          </div>
        </div>
        <div className="flex gap-4 text-xs text-white/50">
          <span>Achetés : {balance?.total_purchased || 0}</span>
          <span>Utilisés : {balance?.total_used || 0}</span>
        </div>
        <p className="text-[11px] text-white/40 mt-3">1 crédit = 1 message avec l'assistant IA</p>
      </div>

      {/* Packs */}
      <h2 className="text-lg font-bold text-gray-900 mb-3">Recharger vos crédits</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="credit-packs">
        {packs.map((pack) => {
          const color = PACK_COLORS[pack.lookup_key] || '#35AFE4';
          const icon = PACK_ICONS[pack.lookup_key] || '💎';
          const isPopular = pack.lookup_key === 'credits_500';
          return (
            <div key={pack.lookup_key}
              className={`relative bg-white rounded-2xl border-2 p-5 transition-all hover:shadow-lg ${isPopular ? 'border-[#7c3aed] ring-1 ring-[#7c3aed]/20' : 'border-gray-200'}`}
              data-testid={`pack-${pack.lookup_key}`}>
              {isPopular && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[#7c3aed] text-white text-[10px] font-bold rounded-full">
                  Populaire
                </span>
              )}
              <div className="text-center">
                <span className="text-3xl mb-2 block">{icon}</span>
                <p className="text-2xl font-bold text-gray-900">{pack.credits}</p>
                <p className="text-xs text-gray-500 mb-3">crédits IA</p>
                <p className="text-xl font-bold mb-1" style={{ color }}>{pack.price_eur}€</p>
                <p className="text-[10px] text-gray-400 mb-4">{(pack.price_eur / pack.credits * 100).toFixed(1)} centimes/crédit</p>
                <button onClick={() => buyPack(pack.lookup_key)}
                  disabled={purchasing === pack.lookup_key}
                  className="w-full py-2.5 rounded-xl text-white text-sm font-bold hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                  style={{ backgroundColor: color }}
                  data-testid={`buy-${pack.lookup_key}`}>
                  {purchasing === pack.lookup_key ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <>Acheter <ArrowRight size={14} /></>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div data-testid="credit-history">
          <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <History size={18} /> Historique des achats
          </h2>
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left text-[10px] uppercase tracking-wider text-gray-400 font-bold px-4 py-2.5">Date</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-gray-400 font-bold px-4 py-2.5">Pack</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-gray-400 font-bold px-4 py-2.5">Montant</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-gray-400 font-bold px-4 py-2.5">Statut</th>
                </tr>
              </thead>
              <tbody>
                {history.map((tx, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="px-4 py-2.5 text-sm text-gray-600">
                      {new Date(tx.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{tx.credits} crédits</td>
                    <td className="px-4 py-2.5 text-sm font-bold text-gray-900">{tx.amount}€</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        tx.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                        tx.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {tx.payment_status === 'paid' ? 'Payé' : tx.payment_status === 'pending' ? 'En attente' : 'Échoué'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
